import React, { useMemo } from 'react'
import MetricCard from './MetricCard'

// Device configuration mapping
const DEVICE_CONFIG = {
  alt1: { icon: 'bi-lightning-charge-fill', color: 'text-warning', border: '#ffc107' },
  alt2: { icon: 'bi-lightning-charge-fill', color: 'text-warning', border: '#ffc107' },
  bat1: { icon: 'bi-battery-full', color: 'text-success', border: '#28a745' },
  bat2: { icon: 'bi-battery-full', color: 'text-success', border: '#28a745' },
  eng1: { icon: 'bi-cpu-fill', color: 'text-primary', border: '#007bff' },
  regulator: { icon: 'bi-cpu-fill', color: 'text-primary', border: '#007bff' },
}

const DEFAULT_CONFIG = { icon: 'bi-gear-fill', color: 'text-secondary', border: '#6c757d' }

const UNITS = {
  volts: 'V', amps: 'A', temp: '°C', field: '%', soc: '%', 
  motorRpm: ' RPM', engineLoad: '%',
}

/**
 * DevicePanel - displays device header with metric cards
 */
const DevicePanel = React.memo(({ 
  device, deviceName, measurements, getChartData, canBusData = null,
  _isChartVisible, _hasRecentData, realTimeData = null, faults = [],
}) => {
  const config = DEVICE_CONFIG[device] ?? DEFAULT_CONFIG
  
  // Memoize chart data map
  const chartDataMap = useMemo(() => {
    const map = new Map()
    measurements?.forEach((measurement) => {
      const key = `${device}-${measurement.measurement}`
      map.set(key, getChartData?.(key) ?? [])
    })
    return map
  }, [device, measurements, getChartData])

  // Memoize metric cards
  const metricCards = useMemo(() => 
    measurements?.map((measurement, index) => {
      const key = `${device}-${measurement.measurement}`
      const chartData = chartDataMap.get(key)
      
      return (
        <div 
          key={key}
          className="chart-wrapper mb-2 col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
          style={{
            animationDelay: `${index * 150}ms`,
            animation: 'fadeInUp 0.6s ease-out both',
          }}
        >
          <MetricCard
            _device={device}
            measurement={measurement.measurement}
            value={measurement._value}
            timestamp={measurement._time}
            unit={UNITS[measurement.measurement] ?? ''}
            chartData={chartData}
            _canBusData={canBusData}
            showChart={_isChartVisible?.(key) ?? true}
            hasRecentData={_hasRecentData?.(key) ?? true}
            realTimeData={realTimeData}
            faults={faults}
            deviceBorderColor={config.border}
            dataSource={measurement.dataSource}
          />
        </div>
      )
    }) ?? [], [device, measurements, chartDataMap, canBusData, realTimeData, _isChartVisible, _hasRecentData, faults, config.border])

  if (!measurements?.length) return null

  // Check boost mode for alternators
  const isBoostMode = device.startsWith('alt') && canBusData?.[`${device}BoostEngaged`] === true

  return (
    <div className="device-panel mb-4">
      <h3 className="mt-4 mb-3 device-header d-flex align-items-center">
        <i 
          className={`${config.icon} ${config.color} me-3`}
          style={{ fontSize: '1.5rem' }}
        />
        {deviceName}
        {isBoostMode && (
          <span className="ms-2" title="Boost Mode Active">
            <i 
              className="bi bi-lightning-charge-fill text-danger" 
              style={{ 
                color: '#ff0000 !important',
                animation: 'pulse 2s infinite',
              }}
            />
            <small className="text-danger fw-bold ms-1">BOOST</small>
          </span>
        )}
      </h3>

      <div className="row g-3 mb-4">
        {metricCards}
      </div>
    </div>
  )
})

DevicePanel.displayName = 'DevicePanel'

export default DevicePanel
